import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  DatePicker,
  Input,
  Label,
} from '@roger/ui-components';
import { ShieldCheck } from 'lucide-react';
import type { SigningAttestation } from './attestations';
import {
  MIN_ESIGN_PASSWORD_LENGTH,
  type ESignatureCredentialInput,
  type SignatureDateInput,
} from './esignatureCredentialApi';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { useESignatureCredential } from './useESignatureCredential';
import { ESignatureResetFields } from './ESignatureResetFields';

/** Local wall-clock time of a lockout expiry, e.g. "2:45 PM". */
function formatLockoutTime(isoTimestamp: string): string {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return 'shortly';
  }
  return parsed.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface AttestationDialogProps {
  open: boolean;
  /** When null (e.g. still loading from API), body shows a loading state. */
  attestation: SigningAttestation | null;
  signerName: string | null;
  signerCredentials?: string | null;
  /** Signer's email. Gives the browser password manager an account to key on. */
  signerEmail?: string | null;
  isLoading?: boolean;
  actionLabel?: string;
  /** Dialog heading. Overridden for non-signing ceremonies (e.g. bypass). */
  title?: string;
  /** Verb shown while the request is in flight, e.g. "Approving…". */
  busyLabel?: string;
  /**
   * Rendered above the attestation text. Used to carry a warning when the
   * ceremony has consequences a plain attestation does not convey.
   */
  warning?: React.ReactNode;
  /**
   * Extra inputs rendered inside the form, above the credential fields — e.g.
   * the bypass reason picker. Kept here rather than in a separate dialog so
   * lockout, first-time setup and reset handling are not duplicated.
   */
  extraFields?: React.ReactNode;
  /** When false, submission is blocked (extraFields are incomplete). */
  extraFieldsValid?: boolean;
  /**
   * Whether this signature takes the signer's e-signature password. True for a
   * clinician signature and for a bypass; false for a QA approval, which is an
   * institutional review rather than the signer's own attestation to the
   * content and so has no password behind it. When false the dialog is
   * attestation-only: no password fields, no credential-status fetch, and no
   * lockout.
   */
  requiresCredential?: boolean;
  /**
   * Whether the signer picks the date they are attesting to.
   *
   * A clinician often documents a visit days after performing it, so the date
   * on the chart has to be theirs to state rather than whatever day the server
   * clock is on. Opt-in: only the clinician-signature dialogs pass this. A QA
   * approval is a review of someone else's work and a bypass is a forfeiture —
   * neither is an attestation to a date.
   *
   * The default comes from the credential-status response this dialog already
   * fetches on open, because that is computed in the agency's timezone and the
   * browser has no way to know it.
   *
   * Callers pass `requiresESignature` rather than `true`. This same dialog
   * serves the QA lane (a case manager signing a submitted chart), where
   * `requiresCredential` is false, no credential status is fetched, and so no
   * default date ever arrives -- an unconditionally required picker would
   * leave that Sign button dead forever. The server applies the same rule:
   * `_create_integrity_signature` keeps a date only when `signer_role` is
   * CLINICIAN.
   */
  showSignatureDate?: boolean;
  /**
   * Patient whose chart is being signed. Only used to resolve the default
   * signing date in that patient's clinic timezone -- the agency timezone has
   * no write path, so without this every clinic reads as Pacific and an
   * Eastern clinic is offered yesterday's date all evening.
   */
  patientId?: string;
  /**
   * Performs the signature. Return a message to show inside this dialog when it
   * fails, or null/undefined on success.
   *
   * The dialog stays open on failure so the typed password is not thrown away,
   * which means a refusal has to render in here: the panels display their own
   * errors in the page body, behind this modal's overlay, where nobody can read
   * them.
   */
  onConfirm: (
    credential: ESignatureCredentialInput,
    /**
     * The declared date, present only when `showSignatureDate` is set. A
     * second argument rather than a field on the credential object: the
     * credential is what proves who is signing, and merging an unrelated
     * clinical value into it would put a date in every request body that
     * spreads it.
     */
    signatureDate?: SignatureDateInput
  ) => void | Promise<string | null | void>;
  onCancel: () => void;
}

/**
 * Pre-sign confirmation dialog.
 *
 * The signer reads and acknowledges the attestation text and re-enters their
 * e-signature password before the actual `/sign` call fires. The client sends
 * `attestation_version` and the password for server validation.
 *
 * A clinician who has no e-signature password yet is asked to create one here
 * rather than being blocked. That is how existing clinicians get a credential
 * without a rollout window where nobody can sign.
 *
 * The fields live in a real `<form>` with a readonly username input and
 * `autoComplete` set, so the browser password manager offers to save the
 * password and can fill it next time. Clinicians sign many charts a day; without
 * this they would forget and reset it constantly.
 */
export const AttestationDialog: React.FC<AttestationDialogProps> = ({
  open,
  attestation,
  signerName,
  signerCredentials,
  signerEmail,
  isLoading = false,
  actionLabel = 'Sign',
  title = 'Confirm Electronic Signature',
  busyLabel = 'Signing…',
  warning,
  extraFields,
  extraFieldsValid = true,
  requiresCredential = true,
  showSignatureDate = false,
  patientId,
  onConfirm,
  onCancel,
}) => {
  const {
    status,
    isLoading: statusLoading,
    error: statusError,
    refresh,
    revalidate,
    isStale,
  } = useESignatureCredential(requiresCredential, patientId);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signatureDate, setSignatureDate] = useState<Date | undefined>();
  // Set once the signer picks a date themselves, so a late-arriving default
  // cannot overwrite a deliberate choice. See the seeding effect below.
  const signatureDateEdited = useRef(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const isFirstTimeSetup =
    requiresCredential && status !== null && !status.has_credential;
  const lockedUntil = requiresCredential
    ? (status?.locked_until ?? null)
    : null;

  // Never leave a typed password sitting in state after the dialog closes.
  useEffect(() => {
    if (!open) {
      setPassword('');
      setConfirmPassword('');
      setSignatureDate(undefined);
      signatureDateEdited.current = false;
      setValidationError(null);
      setSubmitError(null);
      setIsResetting(false);
    }
  }, [open]);

  // Recheck every time the dialog opens. A first signature creates the
  // credential server-side, so a status cached from before that would keep
  // showing the create form and every later signature would be refused with
  // "already set" and no field to enter it in. Silent, so a signer who already
  // has one does not see a loading flash on a dialog they open all day.
  //
  // Submitting is blocked while it is in flight: the visible fields still come
  // from the pre-recheck status, so a fast click would send the shape those
  // fields imply and be refused with a message contradicting what was shown.
  const [isRechecking, setIsRechecking] = useState(false);
  useEffect(() => {
    if (!open || !requiresCredential) {
      return;
    }
    let current = true;
    setIsRechecking(true);
    void revalidate().finally(() => {
      if (current) {
        setIsRechecking(false);
      }
    });
    return () => {
      current = false;
    };
  }, [open, revalidate, requiresCredential]);

  // Seed from the server's answer rather than the browser clock: the default
  // must be today in the AGENCY's timezone, which the client cannot compute.
  // Keyed on the value, so reopening the dialog on a later day re-seeds — the
  // status is re-fetched on every open.
  //
  // The edited guard is what makes that safe. The status is cached at module
  // scope and revalidated asynchronously, so a tab left open across midnight
  // seeds from the stale cache and then receives a *different* date a few
  // hundred milliseconds later. Without the guard that late answer would
  // replace a date the signer had already picked, and they would sign a chart
  // MAC-bound to a date they never chose.
  const defaultSignatureDate = status?.default_signature_date;
  useEffect(() => {
    if (!open || !showSignatureDate || signatureDateEdited.current) {
      return;
    }
    setSignatureDate(parseISOToLocalDate(defaultSignatureDate));
  }, [open, showSignatureDate, defaultSignatureDate]);

  const handleSignatureDateChange = useCallback((next: Date | undefined) => {
    signatureDateEdited.current = true;
    setSignatureDate(next);
  }, []);

  // A wide sanity clamp, not a policy bound. Back- and future-dating are both
  // deliberately allowed, but the field is free-text and the calendar's year
  // dropdown spans decades, so `08/20/2062` is one slip away — and once it is
  // hashed into signature_event the only correction is invalidating the chart
  // and signing it again. A year either side leaves every real ceremony
  // reachable while catching the fat-finger.
  const signatureDateBounds = useMemo(() => {
    const anchor = parseISOToLocalDate(defaultSignatureDate) ?? new Date();
    const min = new Date(anchor);
    min.setFullYear(min.getFullYear() - 1);
    const max = new Date(anchor);
    max.setFullYear(max.getFullYear() + 1);
    return { min, max };
  }, [defaultSignatureDate]);

  const displayName = useMemo(
    () => [signerName, signerCredentials].filter(Boolean).join(', '),
    [signerName, signerCredentials]
  );

  // Everything the credential half of the dialog demands, or vacuously true
  // when this ceremony takes no password. Kept as its own term rather than a
  // branch of canSubmit so that any further condition added below applies to
  // both modes: a gate written into one arm of a ternary silently misses the
  // other, and the password-less arm is the one with no field to explain why
  // the button is dead. `extraFieldsValid` is exactly such a condition — the
  // bypass reason picker must gate the QA-approval mode too.
  const credentialReady =
    !requiresCredential ||
    (!statusLoading &&
      !isRechecking &&
      // A failed recheck means the visible fields may be for the wrong mode, and
      // submitting would be refused with a message no field on screen can satisfy.
      !isStale &&
      lockedUntil === null &&
      password.length > 0 &&
      (!isFirstTimeSetup || confirmPassword.length > 0));

  // Required when shown. Kept as its own term next to credentialReady rather
  // than folded into it, because it gates both password and password-less
  // modes and is not a property of the credential.
  const signatureDateReady = !showSignatureDate || signatureDate !== undefined;

  const canSubmit =
    Boolean(attestation) &&
    extraFieldsValid &&
    !isLoading &&
    credentialReady &&
    signatureDateReady;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // The dialog stays on the page, so the browser must not navigate. Chrome
    // still offers to save the password: it detects the submit and then sees the
    // form leave the DOM when the dialog closes on success.
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    const declaredDate: SignatureDateInput | undefined =
      showSignatureDate && signatureDate
        ? { signature_date: formatDateToISO(signatureDate) }
        : undefined;
    if (!requiresCredential) {
      setValidationError(null);
      setSubmitError(null);
      const noCredentialMessage = await onConfirm({}, declaredDate);
      if (
        typeof noCredentialMessage === 'string' &&
        noCredentialMessage.length > 0
      ) {
        setSubmitError(noCredentialMessage);
      }
      return;
    }
    if (isFirstTimeSetup && password !== confirmPassword) {
      setValidationError('Those passwords do not match.');
      return;
    }
    if (isFirstTimeSetup && password.length < MIN_ESIGN_PASSWORD_LENGTH) {
      // Mirrors the server rule so a too-short password is caught before the
      // request, rather than the dialog closing and losing what was typed.
      setValidationError(
        `Use at least ${MIN_ESIGN_PASSWORD_LENGTH} characters for your e-signature password.`
      );
      return;
    }
    setValidationError(null);
    setSubmitError(null);
    const message = await onConfirm(
      isFirstTimeSetup
        ? { new_esign_password: password }
        : { esign_password: password },
      declaredDate
    );
    if (typeof message === 'string' && message.length > 0) {
      setSubmitError(message);
    }
  };

  const handleResetComplete = async () => {
    // The credential still exists, so the dialog stays in enter-password mode;
    // refresh anyway so a lockout cleared by the reset is reflected.
    await refresh();
    setIsResetting(false);
    setPassword('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => !next && !isLoading && onCancel()}
    >
      {/* The ceremony content (attestation + any warning + credential
          fields) is taller than a laptop viewport, so without a bounded
          height the dialog centres itself and pushes its own top — the
          warning and the first line of the attestation — off-screen. */}
      <DialogContent className='sm:max-w-[560px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <ShieldCheck className='h-5 w-5 text-primary' />
            {title}
          </DialogTitle>
          <DialogDescription className='space-y-3 pt-2 text-sm'>
            {warning}
            <div className='rounded-md border border-border/70 bg-muted/30 px-3 py-3 text-foreground'>
              {attestation ? (
                <p className='whitespace-pre-wrap leading-relaxed'>
                  {attestation.text}
                </p>
              ) : (
                <p className='text-muted-foreground text-sm'>
                  Loading attestation…
                </p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {isResetting ? (
          <ESignatureResetFields
            signerEmail={signerEmail ?? null}
            onDone={handleResetComplete}
            onCancel={() => setIsResetting(false)}
          />
        ) : requiresCredential && statusError ? (
          // Without the status we cannot tell whether to ask for an existing
          // password or to walk them through creating one, and guessing wrong
          // leaves them submitting a request that can only fail.
          <>
            <p className='py-2 text-sm text-destructive'>
              {statusError.message}
            </p>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={onCancel}>
                Cancel
              </Button>
              <Button type='button' onClick={() => void refresh()}>
                Try again
              </Button>
            </DialogFooter>
          </>
        ) : requiresCredential && statusLoading ? (
          // Which fields to show depends on whether this signer already has a
          // password. Waiting avoids flashing "enter your password" at someone
          // who is about to be asked to create one.
          <>
            <p className='py-2 text-sm text-muted-foreground'>
              Checking your e-signature setup…
            </p>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={onCancel}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={e => void handleSubmit(e)} className='space-y-4'>
            {extraFields}
            {/* Readonly, but a real input so the password manager has a
                username to file the entry under. Also doubles as the
                "signing as" line the dialog used to render as plain text. */}
            <div className='space-y-1.5'>
              <Label htmlFor='esign-username'>Signing as</Label>
              <Input
                id='esign-username'
                name='username'
                type='text'
                autoComplete='username'
                readOnly
                value={signerEmail ?? displayName}
                className='bg-muted/40'
              />
              {signerEmail && displayName ? (
                <p className='text-xs text-muted-foreground'>{displayName}</p>
              ) : null}
            </div>

            {!requiresCredential ? null : isFirstTimeSetup ? (
              <>
                <p className='text-sm text-muted-foreground'>
                  Create an e-signature password. You will enter it each time
                  you sign. It is separate from your Roger sign-in password.
                </p>
                <div className='space-y-1.5'>
                  <Label htmlFor='esign-new-password'>
                    New e-signature password
                  </Label>
                  <Input
                    id='esign-new-password'
                    name='new-password'
                    type='password'
                    autoComplete='new-password'
                    autoFocus
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label htmlFor='esign-confirm-password'>
                    Confirm e-signature password
                  </Label>
                  <Input
                    id='esign-confirm-password'
                    name='confirm-new-password'
                    type='password'
                    autoComplete='new-password'
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className='space-y-1.5'>
                <Label htmlFor='esign-password'>E-signature password</Label>
                <Input
                  id='esign-password'
                  name='password'
                  type='password'
                  autoComplete='current-password'
                  autoFocus
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type='button'
                  className='text-xs text-primary hover:underline'
                  onClick={() => setIsResetting(true)}
                >
                  Forgot your e-signature password?
                </button>
              </div>
            )}

            {/* Directly beneath the credential fields, so the date the signer
                is attesting to reads as part of the same act as proving who
                they are — not as a detached form field further up. */}
            {showSignatureDate ? (
              <div className='space-y-1.5'>
                <Label htmlFor='esign-signature-date'>Signature date</Label>
                <DatePicker
                  id='esign-signature-date'
                  aria-label='Signature date'
                  date={signatureDate}
                  onDateChange={handleSignatureDateChange}
                  minDate={signatureDateBounds.min}
                  maxDate={signatureDateBounds.max}
                  // "No date" is not a state a signature can be stored in.
                  allowClear={false}
                  className='w-full'
                />
                <p className='text-xs text-muted-foreground'>
                  Defaults to today in your agency&apos;s time zone. Change it
                  if you are signing for an earlier date.
                </p>
              </div>
            ) : null}

            {requiresCredential && isStale ? (
              <p className='text-sm text-destructive'>
                Could not confirm your e-signature setup.{' '}
                <button
                  type='button'
                  className='underline'
                  onClick={() => void refresh()}
                >
                  Try again
                </button>
                .
              </p>
            ) : null}

            {lockedUntil ? (
              // Otherwise a locked-out clinician finds out one refused attempt
              // at a time, with an enabled button that cannot work.
              <p className='text-sm text-destructive'>
                Too many incorrect attempts. Signing is locked until{' '}
                {formatLockoutTime(lockedUntil)}. Reset your e-signature
                password to unlock it now.
              </p>
            ) : null}

            {validationError ? (
              <p className='text-sm text-destructive'>{validationError}</p>
            ) : null}

            {submitError ? (
              // The server refused the signature (wrong password, lockout, a
              // failed save). It has to be shown here rather than in the panel
              // behind the overlay, or the button just appears to do nothing.
              <p className='text-sm text-destructive'>{submitError}</p>
            ) : null}

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={!canSubmit}>
                {isLoading ? busyLabel : actionLabel}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
